import { Component } from '@theme/component';
import { isValidEmail, subscribeToList, parseConfig } from '@theme/klaviyo-newsletter-api';

/**
 * Klaviyo-backed email signup block.
 *
 * @typedef {object} EmailSignupKlaviyoRefs
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} [consentCheckbox] - The marketing consent checkbox, when enabled.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} errorMessage - The error message element.
 * @property {HTMLElement} successMessage - The success message element.
 *
 * @extends Component<EmailSignupKlaviyoRefs>
 */
class EmailSignupKlaviyo extends Component {
  requiredRefs = ['form', 'emailInput', 'submitButton', 'errorMessage', 'successMessage'];

  #config = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = parseConfig(this);
  }

  get companyId() {
    return this.#config.companyId || '';
  }

  get listIds() {
    return this.#config.listIds ?? [];
  }

  get localeData() {
    if (!this.#config.locale) return null;
    return {
      locale: this.#config.locale,
      language: this.#config.localeLanguage || '',
      country: this.#config.localeCountry || '',
      market: this.#config.market || '',
    };
  }

  handleSubmit = async (event) => {
    event?.preventDefault();

    const { emailInput, submitButton, successMessage, consentCheckbox } = this.refs;

    this.#hideError();
    successMessage.hidden = true;

    if (!emailInput.value || !isValidEmail(emailInput.value)) {
      this.#showError(this.#config.errors?.email);
      emailInput.focus();
      return;
    }

    if (consentCheckbox && !consentCheckbox.checked) {
      this.#showError(this.#config.errors?.consent);
      consentCheckbox.focus();
      return;
    }

    if (!this.companyId || this.listIds.length === 0) {
      console.error('Email signup: missing Klaviyo company ID or list ID.');
      this.#showError(this.#config.errors?.api);
      return;
    }

    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');

    try {
      await Promise.all(
        this.listIds.map((listId) => subscribeToList(emailInput.value, listId, this.companyId, this.localeData)),
      );
      this.#showSuccess();
    } catch (error) {
      console.error('Klaviyo email signup failed:', error);
      this.#showError(this.#config.errors?.api);
    } finally {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {string} [message]
   */
  #showError(message) {
    const { errorMessage, emailInput } = this.refs;
    const text = errorMessage.querySelector('.email-signup-block__message-text');
    if (text) text.textContent = message || '';
    errorMessage.hidden = false;
    emailInput.setAttribute('aria-invalid', 'true');
    emailInput.setAttribute('aria-describedby', errorMessage.id);
  }

  #hideError() {
    const { errorMessage, emailInput } = this.refs;
    errorMessage.hidden = true;
    const text = errorMessage.querySelector('.email-signup-block__message-text');
    if (text) text.textContent = '';
    emailInput.removeAttribute('aria-invalid');
    emailInput.removeAttribute('aria-describedby');
  }

  #showSuccess() {
    const { emailInput, successMessage } = this.refs;
    emailInput.value = '';
    successMessage.hidden = false;
    successMessage.focus();
  }
}

if (!customElements.get('email-signup-klaviyo')) {
  customElements.define('email-signup-klaviyo', EmailSignupKlaviyo);
}

export { EmailSignupKlaviyo };
